import { httpClient } from '@/shared/services/http'
import { API_ENDPOINTS } from '@/shared/constants'
import { config } from '@/config'

export interface GetRolesParams {
    is_active?: boolean
    label?: string
    limit?: number
    offset?: number
}

export interface RolesResponse<T = any> {
    data: T[]
    total?: number
    page?: number
    limit?: number
}

export interface ModuleFeatureApiItem {
    module_id: number
    label: string
    features: Array<{ feature_id: number; label: string; module_id: number }>
}

export interface PermissionApiItem {
    permission_id: number
    code: string
    description: string
    is_active: boolean
    label: string
}

export interface RoleFeaturePermission {
    permission_id: number
    is_allowed: boolean
}

export interface RoleFeature {
    feature_id: number
    permissions: RoleFeaturePermission[]
}

export interface RoleModule {
    module_id: number
    features: RoleFeature[]
}

export interface RoleCreatePayload {
    role_id?: number
    label: string
    role_description: string
    modules: RoleModule[]
    modified_by: string
}

export interface RoleAccessItem {
    access_id: number
    code: string
    role_id: number
    module_id: number
    feature_id: number
    permission_id: number
    is_allowed: boolean
    module_name: string
    feature_name: string
    permission_code: string
}

export interface RoleDetail {
    role_id: number
    role_name: string
    role_description: string
    is_system_role: boolean
    access: RoleAccessItem[]
}

export const rolesApi = {
    getAllRoles: async (params?: GetRolesParams): Promise<RolesResponse | any[]> => {
        const response = await httpClient.get<any>(API_ENDPOINTS.ROLES.GET_ROLES, {
            params,
        })

        // Support both wrapper and bare array response:
        // { data: [...], total: x } or [...]
        if (Array.isArray(response.data)) {
            return response.data
        }

        if (response.data && response.data.data !== undefined) {
            return response.data
        }

        // fallback
        return response.data
    },

    getModulesWithFeatures: async (): Promise<ModuleFeatureApiItem[]> => {
        const response = await httpClient.get<any>(API_ENDPOINTS.ROLES.MODULES_FEATURES)
        if (response.data?.data) {
            return response.data.data
        }
        if (Array.isArray(response.data)) {
            return response.data
        }
        return []
    },

    getPermissions: async (): Promise<PermissionApiItem[]> => {
        const response = await httpClient.get<any>(API_ENDPOINTS.ROLES.PERMISSIONS)
        if (response.data?.data) {
            return response.data.data
        }
        if (Array.isArray(response.data)) {
            return response.data
        }
        return []
    },

    createRole: async (payload: RoleCreatePayload): Promise<any> => {
        const response = await httpClient.post<any>(API_ENDPOINTS.ROLES.CREATE_BULK, payload)
        return response.data
    },

    getRoleById: async (roleId: string | number): Promise<RoleDetail> => {
        // Edit-mode uses access structure payload:
        // /tenants/rbac/roles/:roleId/access-structure
        const response = await httpClient.get<any>(
            `${API_ENDPOINTS.ROLES.GET_ROLES}/${roleId}/access-structure`
        )
        if (response.data?.data) {
            return response.data.data
        }
        return response.data
    },

    toggleActiveRole: async (roleId: string | number): Promise<void> => {
        await httpClient.patch(API_ENDPOINTS.ROLES.TOGGLE_ACTIVE(roleId))
    },

    mapUserRoles: async (userId: string, roleIds: number[]): Promise<any> => {
        const response = await httpClient.post<any>(API_ENDPOINTS.ROLES.USER_ROLE, { user_id: userId, role_ids: roleIds }, {
            headers: { 'x-tenant-id': config.tenantId },
        })
        return response.data
    },
}
